'use client';

import { useState, useEffect, useCallback, useRef } from 'react';
import Link from 'next/link';
import { useFocusTrap } from '@/hooks/useFocusTrap';
import type { SearchResult } from '@/lib/search-index';
import { trackInternalSearch, trackInternalSearchClick } from '@/lib/analytics';

interface SearchModalProps {
  isOpen: boolean;
  onClose: () => void;
}

interface BlogPostSearchResult {
  slug: string;
  title: string;
  description?: string;
  excerpt?: string;
  categories?: string[];
  tags?: string[];
}

interface APISearchResponse {
  query: string;
  count: number;
  results: SearchResult[];
}

interface BlogSearchResponse {
  query: string;
  count: number;
  posts: BlogPostSearchResult[];
}

// Extended type to support instant actions and deep links
type ExtendedSearchResult = SearchResult & {
  isInstant?: boolean;
  instantValue?: string;
  instantAction?: 'copy';
  isRecent?: boolean;
};

const typeColors: Record<SearchResult['type'] | 'instant', string> = {
  service: 'bg-blue-100 text-blue-700',
  industry: 'bg-purple-100 text-purple-700',
  'case-study': 'bg-green-100 text-green-700',
  'knowledge-base': 'bg-yellow-100 text-yellow-700',
  blog: 'bg-pink-100 text-pink-700',
  glossary: 'bg-indigo-100 text-indigo-700',
  tool: 'bg-orange-100 text-orange-700',
  instant: 'bg-emerald-100 text-emerald-700',
};

const typeLabels: Record<SearchResult['type'] | 'instant', string> = {
  service: 'Service',
  industry: 'Industry',
  'case-study': 'Case Study',
  'knowledge-base': 'Guide',
  blog: 'Blog',
  glossary: 'Glossary',
  tool: 'Tool',
  instant: 'Instant',
};

// Recent history localStorage key
const RECENT_TOOLS_KEY = 'inventive_recent_tools';
const MAX_RECENT_TOOLS = 5;

// Helper to generate UUID v4
function generateUUID(): string {
  return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, (c) => {
    const r = Math.random() * 16 | 0;
    const v = c === 'x' ? r : (r & 0x3 | 0x8);
    return v.toString(16);
  });
}

// Deep link mappings for tool-specific queries
interface DeepLinkMapping {
  keywords: string[];
  url: string;
  title: string;
  description: string;
}

const DEEP_LINK_MAPPINGS: DeepLinkMapping[] = [
  // Base64 deep links
  {
    keywords: ['decode base64', 'base64 decode', 'base64 decoder'],
    url: '/tools/base64-encoder-decoder?mode=decode',
    title: 'Decode Base64',
    description: 'Decode Base64 encoded strings back to plain text',
  },
  {
    keywords: ['encode base64', 'base64 encode', 'base64 encoder'],
    url: '/tools/base64-encoder-decoder?mode=encode',
    title: 'Encode Base64',
    description: 'Encode plain text to Base64 format',
  },
  {
    keywords: ['base64 url safe', 'urlsafe base64'],
    url: '/tools/base64-encoder-decoder?mode=encode&encoding=urlsafe',
    title: 'URL-Safe Base64 Encoding',
    description: 'Encode text to URL-safe Base64 format',
  },
  // JSON Formatter deep links
  {
    keywords: ['minify json', 'json minify', 'compress json', 'json compress'],
    url: '/tools/json-formatter?minify=true',
    title: 'Minify JSON',
    description: 'Compress JSON by removing whitespace and formatting',
  },
  {
    keywords: ['beautify json', 'format json', 'json beautify', 'pretty json', 'json pretty'],
    url: '/tools/json-formatter?indent=2',
    title: 'Beautify JSON',
    description: 'Format and beautify JSON with proper indentation',
  },
  {
    keywords: ['sort json keys', 'json sort', 'alphabetize json'],
    url: '/tools/json-formatter?sort=true',
    title: 'Sort JSON Keys',
    description: 'Format JSON with alphabetically sorted keys',
  },
  {
    keywords: ['json tree', 'json tree view', 'visualize json'],
    url: '/tools/json-formatter?view=tree',
    title: 'JSON Tree View',
    description: 'Visualize JSON structure in an interactive tree',
  },
  // Hash generators
  {
    keywords: ['md5 hash', 'generate md5'],
    url: '/tools/hash-generator?algorithm=md5',
    title: 'Generate MD5 Hash',
    description: 'Generate MD5 hash from text input',
  },
  {
    keywords: ['sha256 hash', 'generate sha256', 'sha-256'],
    url: '/tools/hash-generator?algorithm=sha256',
    title: 'Generate SHA-256 Hash',
    description: 'Generate SHA-256 hash from text input',
  },
  {
    keywords: ['sha1 hash', 'generate sha1', 'sha-1'],
    url: '/tools/hash-generator?algorithm=sha1',
    title: 'Generate SHA-1 Hash',
    description: 'Generate SHA-1 hash from text input',
  },
];

// Curated list of popular searches (static to avoid API call on modal open)
const POPULAR_SEARCHES: SearchResult[] = [
  {
    id: 'service-vciso',
    title: 'vCISO',
    description: 'Virtual Chief Information Security Officer services for strategic security leadership',
    url: '/services/vciso',
    type: 'service',
    category: 'Leadership',
  },
  {
    id: 'tool-password-strength-checker',
    title: 'Password Strength Checker',
    description: 'Test your password strength and get recommendations for improvement',
    url: '/tools/security/password-strength-checker',
    type: 'tool',
    category: 'Security',
  },
  {
    id: 'service-security-awareness',
    title: 'Security Awareness Training',
    description: 'Employee security awareness and phishing simulation programs',
    url: '/services/security-awareness',
    type: 'service',
    category: 'Training',
  },
  {
    id: 'tool-subnet-calculator',
    title: 'Subnet Calculator',
    description: 'Calculate IP subnets, CIDR ranges, and network addresses',
    url: '/tools/network/subnet-calculator',
    type: 'tool',
    category: 'Network',
  },
  {
    id: 'industry-healthcare',
    title: 'Healthcare',
    description: 'HIPAA compliance and healthcare cybersecurity solutions',
    url: '/industries/healthcare',
    type: 'industry',
  },
  {
    id: 'glossary-zero-trust',
    title: 'Zero Trust Architecture',
    description: 'A security model requiring continuous verification of every user and device',
    url: '/glossary/zero-trust-architecture',
    type: 'glossary',
    category: 'Security Foundations',
  },
];

// Get recent tools from localStorage
function getRecentTools(): ExtendedSearchResult[] {
  if (typeof window === 'undefined') return [];
  try {
    const stored = localStorage.getItem(RECENT_TOOLS_KEY);
    if (!stored) return [];
    const recent = JSON.parse(stored) as ExtendedSearchResult[];
    return recent.map((r) => ({ ...r, isRecent: true }));
  } catch {
    return [];
  }
}

// Save a tool to recent history
function saveToRecentTools(result: SearchResult): void {
  if (typeof window === 'undefined') return;
  if (result.type !== 'tool') return; // Only save tools

  try {
    const stored = localStorage.getItem(RECENT_TOOLS_KEY);
    let recent: SearchResult[] = stored ? JSON.parse(stored) : [];

    // Remove if already exists (to move to front)
    recent = recent.filter((r) => r.url !== result.url);

    // Add to front
    recent.unshift({
      id: result.id,
      title: result.title,
      description: result.description,
      url: result.url,
      type: result.type,
      category: result.category,
    });

    // Keep only MAX_RECENT_TOOLS
    recent = recent.slice(0, MAX_RECENT_TOOLS);

    localStorage.setItem(RECENT_TOOLS_KEY, JSON.stringify(recent));
  } catch {
    // Silently fail if localStorage is not available
  }
}

export default function SearchModal({ isOpen, onClose }: SearchModalProps) {
  const [query, setQuery] = useState('');
  const [results, setResults] = useState<ExtendedSearchResult[]>([]);
  const [selectedIndex, setSelectedIndex] = useState(0);
  const [isSearching, setIsSearching] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [recentTools, setRecentTools] = useState<ExtendedSearchResult[]>([]);
  const [copiedInstant, setCopiedInstant] = useState<string | null>(null);
  const [userIP, setUserIP] = useState<string | null>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const resultsRef = useRef<HTMLDivElement>(null);
  const modalRef = useRef<HTMLDivElement>(null);

  useFocusTrap(modalRef, isOpen);

  // Load recent tools on mount
  useEffect(() => {
    if (isOpen) {
      setRecentTools(getRecentTools());
    }
  }, [isOpen]);

  // Fetch user IP when searching for it
  useEffect(() => {
    const lowerQuery = query.toLowerCase().trim();
    if ((lowerQuery === 'ip' || lowerQuery === 'my ip' || lowerQuery === 'what is my ip' || lowerQuery === 'myip') && !userIP) {
      fetch('https://api.ipify.org?format=json')
        .then((res) => res.json())
        .then((data) => setUserIP(data.ip))
        .catch(() => setUserIP(null));
    }
  }, [query, userIP]);

  // Check for instant results based on query
  const getInstantResults = useCallback((searchQuery: string): ExtendedSearchResult[] => {
    const lowerQuery = searchQuery.toLowerCase().trim();
    const instantResults: ExtendedSearchResult[] = [];

    // UUID generation
    if (lowerQuery === 'uuid' || lowerQuery === 'generate uuid' || lowerQuery === 'new uuid' || lowerQuery === 'guid') {
      const uuid = generateUUID();
      instantResults.push({
        id: 'instant-uuid',
        title: `UUID: ${uuid}`,
        description: 'Click to copy this generated UUID to clipboard',
        url: '#',
        type: 'tool',
        isInstant: true,
        instantValue: uuid,
        instantAction: 'copy',
      });
    }

    // IP address lookup
    if (lowerQuery === 'ip' || lowerQuery === 'my ip' || lowerQuery === 'what is my ip' || lowerQuery === 'myip') {
      if (userIP) {
        instantResults.push({
          id: 'instant-ip',
          title: `Your IP: ${userIP}`,
          description: 'Click to copy your public IP address to clipboard',
          url: '#',
          type: 'tool',
          isInstant: true,
          instantValue: userIP,
          instantAction: 'copy',
        });
      } else {
        instantResults.push({
          id: 'instant-ip-loading',
          title: 'Your IP: Loading...',
          description: 'Fetching your public IP address',
          url: '#',
          type: 'tool',
          isInstant: true,
        });
      }
    }

    return instantResults;
  }, [userIP]);

  // Check for deep link matches
  const getDeepLinkResults = useCallback((searchQuery: string): ExtendedSearchResult[] => {
    const lowerQuery = searchQuery.toLowerCase().trim();
    const deepLinkResults: ExtendedSearchResult[] = [];

    for (const mapping of DEEP_LINK_MAPPINGS) {
      if (mapping.keywords.some((kw) => lowerQuery.includes(kw) || kw.includes(lowerQuery))) {
        deepLinkResults.push({
          id: `deeplink-${mapping.url}`,
          title: mapping.title,
          description: mapping.description,
          url: mapping.url,
          type: 'tool',
        });
      }
    }

    return deepLinkResults;
  }, []);

  // Handle search with debounce
  useEffect(() => {
    if (!query || query.trim().length < 2) {
      setResults([]);
      setSelectedIndex(0);
      setError(null);
      return;
    }

    // Check for instant results immediately (no debounce)
    const instantResults = getInstantResults(query);
    const deepLinkResults = getDeepLinkResults(query);

    // If we have instant results, show them immediately
    if (instantResults.length > 0 || deepLinkResults.length > 0) {
      setResults([...instantResults, ...deepLinkResults]);
      setSelectedIndex(0);
    }

    setIsSearching(true);
    setError(null);

    const timer = setTimeout(async () => {
      try {
        // Fetch from both search APIs in parallel
        const [searchResponse, blogResponse] = await Promise.allSettled([
          fetch(`/api/search?q=${encodeURIComponent(query)}&limit=15`),
          fetch(`/api/blog/search?q=${encodeURIComponent(query)}`),
        ]);

        let allResults: ExtendedSearchResult[] = [];

        // Handle main search API results (glossary, tools, etc.)
        if (searchResponse.status === 'fulfilled' && searchResponse.value.ok) {
          const data: APISearchResponse = await searchResponse.value.json();
          allResults = [...allResults, ...data.results];
        }

        // Handle blog search results
        if (blogResponse.status === 'fulfilled' && blogResponse.value.ok) {
          const blogData: BlogSearchResponse = await blogResponse.value.json();
          const blogResults: ExtendedSearchResult[] = (blogData.posts || []).slice(0, 5).map((post) => ({
            id: `blog-${post.slug}`,
            title: post.title,
            description: post.description || post.excerpt || '',
            url: `/blog/${post.slug}`,
            type: 'blog' as const,
            category: post.categories?.[0] || undefined,
            tags: post.tags || [],
          }));
          allResults = [...allResults, ...blogResults];
        }

        // Add instant results at the top
        const currentInstantResults = getInstantResults(query);
        const currentDeepLinkResults = getDeepLinkResults(query);

        // Deduplicate by id (including deep links that might match regular results)
        const seen = new Set<string>();
        // Add instant and deep link IDs first
        currentInstantResults.forEach((r) => seen.add(r.id));
        currentDeepLinkResults.forEach((r) => seen.add(r.id));
        // Also mark deep link URLs as seen to avoid duplicates from API
        currentDeepLinkResults.forEach((r) => seen.add(r.url));

        const uniqueApiResults = allResults.filter((result) => {
          if (seen.has(result.id)) return false;
          if (seen.has(result.url)) return false;
          seen.add(result.id);
          return true;
        });

        // Sort API results by relevance - put tools and glossary first when searching
        uniqueApiResults.sort((a, b) => {
          const priority: Record<string, number> = {
            tool: 1,
            glossary: 2,
            service: 3,
            blog: 4,
            industry: 5,
            'case-study': 6,
            'knowledge-base': 7,
          };
          return (priority[a.type] || 10) - (priority[b.type] || 10);
        });

        // Combine: instant results first, then deep links, then API results
        const combinedResults = [
          ...currentInstantResults,
          ...currentDeepLinkResults,
          ...uniqueApiResults.slice(0, 15 - currentInstantResults.length - currentDeepLinkResults.length),
        ];

        setResults(combinedResults);

        // Track search with results count
        trackInternalSearch(query, combinedResults.length, 'command_palette');

        // Show error if no results from either API
        if (combinedResults.length === 0 && searchResponse.status === 'rejected' && blogResponse.status === 'rejected') {
          setError('Search unavailable. Please try again later.');
        }
      } catch (err) {
        console.error('Search error:', err);
        setError('Search failed. Please try again.');
        // Keep instant results if we have them
        const currentInstantResults = getInstantResults(query);
        const currentDeepLinkResults = getDeepLinkResults(query);
        if (currentInstantResults.length > 0 || currentDeepLinkResults.length > 0) {
          setResults([...currentInstantResults, ...currentDeepLinkResults]);
        } else {
          setResults([]);
        }
      }

      setSelectedIndex(0);
      setIsSearching(false);
    }, 200);

    return () => clearTimeout(timer);
  }, [query, getInstantResults, getDeepLinkResults]);

  // Focus input when modal opens
  useEffect(() => {
    if (isOpen && inputRef.current) {
      inputRef.current.focus();
    }
  }, [isOpen]);

  // Handle instant action (copy to clipboard)
  const handleInstantAction = useCallback(async (result: ExtendedSearchResult) => {
    if (result.instantAction === 'copy' && result.instantValue) {
      try {
        await navigator.clipboard.writeText(result.instantValue);
        setCopiedInstant(result.id);
        setTimeout(() => setCopiedInstant(null), 2000);
      } catch (err) {
        console.error('Failed to copy:', err);
      }
    }
  }, []);

  // Handle result selection (navigation or instant action)
  const handleResultSelect = useCallback((result: ExtendedSearchResult, index?: number) => {
    if (result.isInstant && result.instantAction) {
      handleInstantAction(result);
      return;
    }

    // Track search result click
    if (query.trim()) {
      trackInternalSearchClick(
        query,
        result.url,
        index !== undefined ? index + 1 : selectedIndex + 1,
        result.type as 'tool' | 'blog' | 'service' | 'kb' | 'glossary'
      );
    }

    // Save to recent history if it's a tool
    if (result.type === 'tool' && !result.isInstant) {
      saveToRecentTools(result);
    }

    onClose();
    window.location.href = result.url;
  }, [handleInstantAction, onClose, query, selectedIndex]);

  // Handle keyboard navigation
  const handleKeyDown = useCallback(
    (e: KeyboardEvent) => {
      if (!isOpen) return;

      // Calculate what results are currently being displayed
      const displayedResults = results.length > 0
        ? results
        : query.length === 0
          ? [...(recentTools.length > 0 ? recentTools : []), ...POPULAR_SEARCHES]
          : [];

      switch (e.key) {
        case 'Escape':
          onClose();
          break;
        case 'ArrowDown':
          e.preventDefault();
          setSelectedIndex((prev) =>
            prev < displayedResults.length - 1 ? prev + 1 : prev
          );
          break;
        case 'ArrowUp':
          e.preventDefault();
          setSelectedIndex((prev) => (prev > 0 ? prev - 1 : 0));
          break;
        case 'Enter':
          e.preventDefault();
          const selectedResult = displayedResults[selectedIndex];
          if (selectedResult) {
            handleResultSelect(selectedResult);
          }
          break;
      }
    },
    [isOpen, results, selectedIndex, onClose, query, recentTools, handleResultSelect]
  );

  useEffect(() => {
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [handleKeyDown]);

  // Scroll selected item into view
  useEffect(() => {
    if (resultsRef.current) {
      const selectedElement = resultsRef.current.children[selectedIndex] as HTMLElement;
      if (selectedElement) {
        selectedElement.scrollIntoView({ block: 'nearest', behavior: 'smooth' });
      }
    }
  }, [selectedIndex]);

  if (!isOpen) return null;

  // Build display results with recent tools section
  const displayResults: ExtendedSearchResult[] = results.length > 0
    ? results
    : query.length === 0
      ? [...(recentTools.length > 0 ? recentTools : []), ...POPULAR_SEARCHES]
      : [];

  return (
    <div className="fixed inset-0 z-50 flex items-start justify-center pt-20 px-4">
      {/* Backdrop */}
      <div className="fixed inset-0 bg-black/50 backdrop-blur-sm" onClick={onClose} aria-hidden="true" />

      {/* Modal */}
      <div ref={modalRef} role="dialog" aria-modal="true" aria-label="Search" className="relative bg-white rounded-xl shadow-2xl w-full max-w-2xl max-h-[600px] flex flex-col">
        {/* Search Input */}
        <div className="p-4 border-b border-gray-200">
          <div className="flex items-center gap-3">
            <svg
              className="w-5 h-5 text-gray-400"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
              />
            </svg>
            <input
              ref={inputRef}
              type="text"
              aria-label="Search services, industries, tools, guides, and blog posts"
              placeholder="Search services, industries, tools, guides, blog posts..."
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              className="flex-1 text-lg outline-none focus:ring-2 focus:ring-[#2813e8] text-gray-900 placeholder-gray-400"
            />
            {isSearching && (
              <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-blue-600" />
            )}
            <button
              onClick={onClose}
              className="text-gray-400 hover:text-gray-600 text-sm font-medium"
            >
              ESC
            </button>
          </div>
        </div>

        {/* Results */}
        <div ref={resultsRef} className="flex-1 overflow-y-auto" aria-live="polite" aria-atomic="true">
          {error ? (
            <div className="flex flex-col items-center justify-center py-12 text-gray-500">
              <svg
                className="w-16 h-16 mb-4 text-red-300"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"
                />
              </svg>
              <p className="text-lg font-medium">{error}</p>
            </div>
          ) : displayResults.length > 0 ? (
            <div className="py-2">
              {/* Recent Tools Section */}
              {query.length === 0 && recentTools.length > 0 && (
                <>
                  <div className="px-4 py-2 text-xs font-semibold text-gray-500 uppercase tracking-wider flex items-center gap-2">
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                    Recent Tools
                  </div>
                  {recentTools.map((result, index) => (
                    <button
                      key={`recent-${result.id}`}
                      onClick={() => handleResultSelect(result)}
                      className={`w-full text-left block px-4 py-3 hover:bg-gray-50 transition-colors ${
                        index === selectedIndex ? 'bg-blue-50' : ''
                      }`}
                    >
                      <div className="flex items-start gap-3">
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2 mb-1">
                            <span className="font-semibold text-gray-900 truncate">{result.title}</span>
                            <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${typeColors[result.type]}`}>
                              {typeLabels[result.type]}
                            </span>
                          </div>
                          <p className="text-sm text-gray-600 line-clamp-2">{result.description}</p>
                        </div>
                        <svg className="w-5 h-5 text-gray-400 flex-shrink-0 mt-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                        </svg>
                      </div>
                    </button>
                  ))}
                  <div className="border-t border-gray-100 my-2" />
                </>
              )}

              {/* Popular Searches Header */}
              {query.length === 0 && (
                <div className="px-4 py-2 text-xs font-semibold text-gray-500 uppercase tracking-wider">
                  Popular Searches
                </div>
              )}

              {/* Instant Results indicator */}
              {query.length > 0 && results.some((r) => r.isInstant) && (
                <div className="px-4 py-2 text-xs font-semibold text-emerald-600 uppercase tracking-wider flex items-center gap-2">
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
                  </svg>
                  Instant Results
                </div>
              )}

              {/* Results List */}
              {displayResults.map((result, index) => {
                // Skip recent tools in the main list (they're shown above)
                if (query.length === 0 && result.isRecent) return null;

                const adjustedIndex = query.length === 0 && recentTools.length > 0
                  ? index
                  : index;

                // Render instant result (copyable)
                if (result.isInstant) {
                  return (
                    <button
                      key={result.id}
                      onClick={() => handleInstantAction(result)}
                      className={`w-full text-left block px-4 py-3 hover:bg-emerald-50 transition-colors ${
                        adjustedIndex === selectedIndex ? 'bg-emerald-50' : ''
                      }`}
                    >
                      <div className="flex items-start gap-3">
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2 mb-1">
                            <span className="font-semibold text-gray-900 truncate font-mono">
                              {result.title}
                            </span>
                            <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${typeColors.instant}`}>
                              {copiedInstant === result.id ? 'Copied!' : 'Click to Copy'}
                            </span>
                          </div>
                          <p className="text-sm text-gray-600 line-clamp-2">{result.description}</p>
                        </div>
                        <svg
                          className={`w-5 h-5 flex-shrink-0 mt-1 ${copiedInstant === result.id ? 'text-emerald-500' : 'text-gray-400'}`}
                          fill="none"
                          stroke="currentColor"
                          viewBox="0 0 24 24"
                        >
                          {copiedInstant === result.id ? (
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                          ) : (
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 5H6a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2v-1M8 5a2 2 0 002 2h2a2 2 0 002-2M8 5a2 2 0 012-2h2a2 2 0 012 2m0 0h2a2 2 0 012 2v3m2 4H10m0 0l3-3m-3 3l3 3" />
                          )}
                        </svg>
                      </div>
                    </button>
                  );
                }

                // Render regular result (navigable)
                return (
                  <Link
                    key={result.id}
                    href={result.url}
                    onClick={() => {
                      // Track search result click
                      if (query.trim()) {
                        trackInternalSearchClick(
                          query,
                          result.url,
                          index + 1,
                          result.type as 'tool' | 'blog' | 'service' | 'kb' | 'glossary'
                        );
                      }
                      if (result.type === 'tool') {
                        saveToRecentTools(result);
                      }
                      onClose();
                    }}
                    className={`block px-4 py-3 hover:bg-gray-50 transition-colors ${
                      adjustedIndex === selectedIndex ? 'bg-blue-50' : ''
                    }`}
                  >
                    <div className="flex items-start gap-3">
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 mb-1">
                          <span className="font-semibold text-gray-900 truncate">{result.title}</span>
                          <span
                            className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${
                              typeColors[result.type]
                            }`}
                          >
                            {typeLabels[result.type]}
                          </span>
                        </div>
                        <p className="text-sm text-gray-600 line-clamp-2">{result.description}</p>
                        {result.category && (
                          <p className="text-xs text-gray-500 mt-1">Category: {result.category}</p>
                        )}
                      </div>
                      <svg
                        className="w-5 h-5 text-gray-400 flex-shrink-0 mt-1"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M9 5l7 7-7 7"
                        />
                      </svg>
                    </div>
                  </Link>
                );
              })}
            </div>
          ) : query.length >= 2 && !isSearching ? (
            <div className="flex flex-col items-center justify-center py-12 text-gray-500">
              <svg
                className="w-16 h-16 mb-4 text-gray-300"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M9.172 16.172a4 4 0 015.656 0M9 10h.01M15 10h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                />
              </svg>
              <p className="text-lg font-medium">No results found</p>
              <p className="text-sm mt-1">Try searching for services, industries, tools, or blog topics</p>
            </div>
          ) : (
            <div className="flex flex-col items-center justify-center py-12 text-gray-500">
              <svg
                className="w-16 h-16 mb-4 text-gray-300"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
                />
              </svg>
              <p className="text-lg font-medium">Start typing to search</p>
              <p className="text-sm mt-1">Search across services, industries, tools, guides, and blog posts</p>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="px-4 py-3 border-t border-gray-200 bg-gray-50 rounded-b-xl">
          <div className="flex items-center justify-between text-xs text-gray-500">
            <div className="flex items-center gap-4">
              <span className="flex items-center gap-1">
                <kbd className="px-2 py-1 bg-white border border-gray-300 rounded text-xs">↑</kbd>
                <kbd className="px-2 py-1 bg-white border border-gray-300 rounded text-xs">↓</kbd>
                <span>Navigate</span>
              </span>
              <span className="flex items-center gap-1">
                <kbd className="px-2 py-1 bg-white border border-gray-300 rounded text-xs">Enter</kbd>
                <span>Select</span>
              </span>
              <span className="flex items-center gap-1">
                <kbd className="px-2 py-1 bg-white border border-gray-300 rounded text-xs">ESC</kbd>
                <span>Close</span>
              </span>
            </div>
            <span>{displayResults.length} results</span>
          </div>
        </div>
      </div>
    </div>
  );
}
